import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type UserItem = {
  id: number;
  username: string;
  email: string;
  role: string;
  department: string;
};

function UsersPage() {
  const navigate = useNavigate();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState<number | null>(null);

  // form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("employee");
  const [department, setDepartment] = useState("");
  const [customDepartment, setCustomDepartment] = useState("");

  // users list
  const [users, setUsers] = useState<UserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [createLoading, setCreateLoading] = useState(false);
  const [createErrorMessage, setCreateErrorMessage] = useState("");
  const [deleteLoadingId, setDeleteLoadingId] = useState<number | null>(null);
  const [selectedDepartmentFilter, setSelectedDepartmentFilter] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const departmentOptions = [
    "JMC",
    "企画推進室",
    "DX事業推進室",
    "総務",
    "美容決済",
    "保健情報"
  ];

  const roleLabels: Record<string, string> = {
    employee: "社員",
    admin: "管理者"
  };

  const roleBadgeStyles: Record<string, { backgroundColor: string; color: string }> = {
    employee: {
      backgroundColor: "#dbeafe",
      color: "#1d4ed8"
    },
    admin: {
      backgroundColor: "#fee2e2",
      color: "#dc2626"
    }
  };

  const filteredUsers = users.filter((user) => {
    const matchesDepartment =
      !selectedDepartmentFilter || user.department === selectedDepartmentFilter;

    const matchesRole =
      !selectedRoleFilter || user.role === selectedRoleFilter;

    const keyword = searchKeyword.trim().toLowerCase();

    const matchesSearch =
      !keyword || user.username.toLowerCase().includes(keyword);

    return matchesDepartment && matchesRole && matchesSearch;
  });

  const dynamicDepartmentFilterOptions = [
    ...departmentOptions,
    ...users
      .map((user) => user.department)
      .filter(
        (departmentName) =>
          departmentName &&
          !departmentOptions.includes(departmentName)
      )
  ];

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await fetch("/users/");

      if (!response.ok) {
        throw new Error("ユーザー一覧の取得に失敗しました");
      }

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setErrorMessage("ユーザー一覧の取得に失敗しました");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async () => {
    try {
      setCreateLoading(true);
      setCreateErrorMessage("");

      const finalDepartment =
        customDepartment.trim() !== "" ? customDepartment.trim() : department;

      if (!name.trim()) {
        setCreateErrorMessage("名前を入力してください");
        return;
      }

      if (!email.trim()) {
        setCreateErrorMessage("メールアドレスを入力してください");
        return;
      }

      if (!password.trim()) {
        setCreateErrorMessage("仮パスワードを入力してください");
        return;
      }

      if (!role.trim()) {
        setCreateErrorMessage("役割を選択してください");
        return;
      }

      if (!finalDepartment.trim()) {
        setCreateErrorMessage("部署を選択または入力してください");
        return;
      }

      const response = await fetch("/users/register", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: name,
          email: email,
          password: password,
          role: role,
          department: finalDepartment
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (Array.isArray(data.detail)) {
          setCreateErrorMessage(data.detail[0].msg);
        } else {
          setCreateErrorMessage(data.detail || "ユーザー作成に失敗しました");
        }
        return;
      }

      setName("");
      setEmail("");
      setPassword("");
      setRole("employee");
      setDepartment("");
      setCustomDepartment("");
      setShowCreateModal(false);

      await fetchUsers();
    } catch (error) {
      console.error("Failed to create user:", error);
      setCreateErrorMessage("ユーザー作成に失敗しました");
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    const isConfirmed = window.confirm(
      `「${username}」を削除してもよろしいですか？`
    );

    if (!isConfirmed) {
      return;
    }

    try {
      setDeleteLoadingId(userId);
      setErrorMessage("");

      const response = await fetch(`/users/${userId}`, {
        method: "DELETE"
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.detail || "ユーザー削除に失敗しました");
        return;
      }

      await fetchUsers();
    } catch (error) {
      console.error("Failed to delete user:", error);
      setErrorMessage("ユーザー削除に失敗しました");
    } finally {
      setDeleteLoadingId(null);
    }
  };

  const handleOpenEditModal = (user: UserItem) => {
    setIsEditMode(true);
    setEditingUserId(user.id);
    setCreateErrorMessage("");

    setName(user.username);
    setEmail(user.email);
    setPassword("");
    setRole(user.role);

    if (departmentOptions.includes(user.department)) {
      setDepartment(user.department);
      setCustomDepartment("");
    } else {
      setDepartment("");
      setCustomDepartment(user.department);
    }

    setShowCreateModal(true);
  };

  const handleUpdateUser = async () => {
    try {
      setCreateLoading(true);
      setCreateErrorMessage("");

      const finalDepartment =
        customDepartment.trim() !== "" ? customDepartment.trim() : department;

      if (!editingUserId) {
        setCreateErrorMessage("編集対象のユーザーが見つかりません");
        return;
      }

      if (!name.trim()) {
        setCreateErrorMessage("名前を入力してください");
        return;
      }

      if (!email.trim()) {
        setCreateErrorMessage("メールアドレスを入力してください");
        return;
      }

      if (!role.trim()) {
        setCreateErrorMessage("役割を選択してください");
        return;
      }

      if (!finalDepartment.trim()) {
        setCreateErrorMessage("部署を選択または入力してください");
        return;
      }

      const response = await fetch(`/users/${editingUserId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          username: name,
          email: email,
          role: role,
          department: finalDepartment
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (Array.isArray(data.detail)) {
          setCreateErrorMessage(data.detail[0].msg);
        } else {
          setCreateErrorMessage(data.detail || "ユーザー更新に失敗しました");
        }
        return;
      }

      setName("");
      setEmail("");
      setPassword("");
      setRole("employee");
      setDepartment("");
      setCustomDepartment("");
      setIsEditMode(false);
      setEditingUserId(null);
      setShowCreateModal(false);

      await fetchUsers();
    } catch (error) {
      console.error("Failed to update user:", error);
      setCreateErrorMessage("ユーザー更新に失敗しました");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#f3f4f6",
        padding: "32px",
        fontFamily: "Arial, sans-serif"
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px"
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: "28px",
              color: "#111827",
              fontWeight: 700
            }}
          >
            ユーザー管理
          </h1>
          <p
            style={{
              margin: "8px 0 0 0",
              fontSize: "14px",
              color: "#6b7280"
            }}
          >
            ユーザーの作成・閲覧・編集・削除を行います
          </p>
        </div>

        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <button
            onClick={() => navigate("/admin-dashboard")}
            style={{
              border: "1px solid #cbd5e1",
              backgroundColor: "white",
              color: "#0f172a",
              borderRadius: "12px",
              padding: "12px 18px",
              fontSize: "14px",
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            ← ダッシュボードへ戻る
          </button>

          <button
            onClick={() => {
              setIsEditMode(false);
              setEditingUserId(null);
              setCreateErrorMessage("");
              setName("");
              setEmail("");
              setPassword("");
              setRole("employee");
              setDepartment("");
              setCustomDepartment("");
              setShowCreateModal(true);
            }}
            style={{
  backgroundColor: "#0f766e",
  color: "white",
  border: "none",
  borderRadius: "14px",
  padding: "13px 22px",
  fontSize: "14px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(15, 118, 110, 0.18)"
}}
          >
            ＋ 新規ユーザー作成
          </button>
        </div>
      </div>

      <div
        style={{
          backgroundColor: "white",
          borderRadius: "20px",
          padding: "24px",
          boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
          border: "1px solid #eef2f7"
        }}
      >
        <div style={filterWrap}>
          <div style={filterGrid}>
            <div>
              <div style={filterLabel}>部署</div>
              <select
                value={selectedDepartmentFilter}
                onChange={(e) => setSelectedDepartmentFilter(e.target.value)}
                style={inputStyle}
              >
                <option value="">すべての部署</option>
                {dynamicDepartmentFilterOptions.map((departmentName, index) => (
                  <option key={index} value={departmentName}>
                    {departmentName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={filterLabel}>役割</div>
              <select
                value={selectedRoleFilter}
                onChange={(e) => setSelectedRoleFilter(e.target.value)}
                style={inputStyle}
              >
                <option value="">すべての役割</option>
                <option value="employee">社員</option>
                <option value="admin">管理者</option>
              </select>
            </div>

            <div>
              <div style={filterLabel}>検索（名前）</div>
              <input
                type="text"
                placeholder="名前で検索"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={filterLabel}>　</div>
              <button
                onClick={() => {
                  setSelectedDepartmentFilter("");
                  setSelectedRoleFilter("");
                  setSearchKeyword("");
                }}
                style={btnSecondary}
              >
                クリア
              </button>
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 12, color: "#64748b", fontWeight: 800 }}>
            表示件数：{filteredUsers.length} 件
          </div>
        </div>

        {loading ? (
          <div
            style={{
              padding: "32px 12px",
              textAlign: "center",
              color: "#6b7280",
              fontSize: "14px",
              fontWeight: 600
            }}
          >
            ユーザー一覧を読み込み中です...
          </div>
        ) : errorMessage ? (
          <div
            style={{
              padding: "16px",
              marginBottom: "12px",
              borderRadius: "12px",
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              fontSize: "14px",
              fontWeight: 600,
              border: "1px solid #fecaca"
            }}
          >
            {errorMessage}
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse"
            }}
          >
            <thead>
              <tr
                style={{
                  textAlign: "left",
                  borderBottom: "1px solid #e5e7eb"
                }}
              >
                <th style={{ padding: "14px", color: "#374151", fontSize: "14px" }}>
                  名前
                </th>
                <th style={{ padding: "14px", color: "#374151", fontSize: "14px" }}>
                  メールアドレス
                </th>
                <th style={{ padding: "14px", color: "#374151", fontSize: "14px" }}>
                  役割
                </th>
                <th style={{ padding: "14px", color: "#374151", fontSize: "14px" }}>
                  部署
                </th>
                <th style={{ padding: "14px", color: "#374151", fontSize: "14px" }}>
                  操作
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      padding: "24px",
                      textAlign: "center",
                      color: "#6b7280",
                      fontSize: "14px",
                      fontWeight: 600
                    }}
                  >
                    条件に一致するユーザーがありません
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user, index) => (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom:
                        index !== filteredUsers.length - 1
                          ? "1px solid #f1f5f9"
                          : "none"
                    }}
                  >
                    <td style={{ padding: "16px", color: "#111827" }}>
                      {user.username}
                    </td>
                    <td style={{ padding: "16px", color: "#6b7280" }}>
                      {user.email}
                    </td>
                    <td style={{ padding: "16px" }}>
                      <span
                        style={{
                          backgroundColor: roleBadgeStyles[user.role]?.backgroundColor || "#f3f4f6",
                          color: roleBadgeStyles[user.role]?.color || "#374151",
                          borderRadius: "999px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 700,
                          display: "inline-block"
                        }}
                      >
                        {roleLabels[user.role] || user.role}
                      </span>
                    </td>

                    <td style={{ padding: "16px" }}>
                      <span
                        style={{
                          backgroundColor: "#e0f2fe",
                          color: "#0369a1",
                          borderRadius: "999px",
                          padding: "6px 12px",
                          fontSize: "12px",
                          fontWeight: 700,
                          display: "inline-block"
                        }}
                      >
                        {user.department || "未設定"}
                      </span>
                    </td>

                    <td style={{ padding: "16px" }}>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <button
                          onClick={() => handleOpenEditModal(user)}
                          style={{
                            border: "none",
                            backgroundColor: "#eff6ff",
                            color: "#2563eb",
                            padding: "8px 12px",
                            borderRadius: "10px",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: "pointer"
                          }}
                        >
                          編集
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          disabled={deleteLoadingId === user.id}
                          style={{
                            border: "none",
                            backgroundColor: "#fef2f2",
                            color: "#dc2626",
                            padding: "8px 12px",
                            borderRadius: "10px",
                            fontSize: "12px",
                            fontWeight: 700,
                            cursor: deleteLoadingId === user.id ? "not-allowed" : "pointer",
                            opacity: deleteLoadingId === user.id ? 0.7 : 1
                          }}
                        >
                          {deleteLoadingId === user.id ? "削除中..." : "削除"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {showCreateModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "24px",
            zIndex: 999,
            boxSizing: "border-box"
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              backgroundColor: "white",
              borderRadius: "24px",
              boxShadow: "0 20px 50px rgba(15, 23, 42, 0.18)",
              border: "1px solid #e5e7eb",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
          >
            <div
              style={{
                padding: "24px 28px 18px 28px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                backgroundColor: "white"
              }}
            >
              <div>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    color: "#111827",
                    fontWeight: 700
                  }}
                >
                  {isEditMode ? "ユーザー編集" : "新規ユーザー作成"}
                </h2>
                <p
                  style={{
                    margin: "8px 0 0 0",
                    fontSize: "14px",
                    color: "#6b7280"
                  }}
                >
                  管理者が仮パスワードを設定し、ユーザーは初回ログイン後に自分のパスワードへ変更します
                </p>
              </div>

              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  border: "none",
                  backgroundColor: "#f3f4f6",
                  color: "#374151",
                  width: "36px",
                  height: "36px",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "18px",
                  fontWeight: 700,
                  flexShrink: 0
                }}
              >
                ×
              </button>
            </div>

            <div
              style={{
                padding: "24px 28px",
                overflowY: "auto"
              }}
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                {createErrorMessage && (
                  <div
                    style={{
                      padding: "14px 16px",
                      borderRadius: "12px",
                      backgroundColor: "#fef2f2",
                      color: "#dc2626",
                      fontSize: "14px",
                      fontWeight: 600,
                      border: "1px solid #fecaca"
                    }}
                  >
                    {createErrorMessage}
                  </div>
                )}

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px"
                    }}
                  >
                    名前
                  </label>
                  <input
                    type="text"
                    placeholder="名前を入力"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    style={{
                      width: "100%",
                      height: "46px",
                      borderRadius: "12px",
                      border: "1px solid #d1d5db",
                      padding: "0 14px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px"
                    }}
                  >
                    メールアドレス
                  </label>
                  <input
                    type="email"
                    placeholder="example@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      width: "100%",
                      height: "46px",
                      borderRadius: "12px",
                      border: "1px solid #d1d5db",
                      padding: "0 14px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                {!isEditMode && (
                  <>
                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: 700,
                          color: "#374151",
                          marginBottom: "8px"
                        }}
                      >
                        仮パスワード
                      </label>
                      <input
                        type="password"
                        placeholder="管理者が仮パスワードを設定"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{
                          width: "100%",
                          height: "46px",
                          borderRadius: "12px",
                          border: "1px solid #d1d5db",
                          padding: "0 14px",
                          fontSize: "14px",
                          outline: "none",
                          boxSizing: "border-box"
                        }}
                      />
                    </div>

                    <div
                      style={{
                        backgroundColor: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        borderRadius: "14px",
                        padding: "14px 16px",
                        color: "#1d4ed8",
                        fontSize: "13px",
                        lineHeight: 1.6
                      }}
                    >
                      この仮パスワードは初回ログイン時のみ使用します。ログイン後、ユーザー自身が新しいパスワードを設定します。
                    </div>
                  </>
                )}

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px"
                    }}
                  >
                    役割
                  </label>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    style={{
                      width: "100%",
                      height: "46px",
                      borderRadius: "12px",
                      border: "1px solid #d1d5db",
                      padding: "0 14px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box",
                      backgroundColor: "white"
                    }}
                  >
                    <option value="employee">employee</option>
                    <option value="admin">admin</option>
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px"
                    }}
                  >
                    部署（既存から選択）
                  </label>
                  <select
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    style={{
                      width: "100%",
                      height: "46px",
                      borderRadius: "12px",
                      border: "1px solid #d1d5db",
                      padding: "0 14px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box",
                      backgroundColor: "white"
                    }}
                  >
                    <option value="">部署を選択してください</option>
                    {departmentOptions.map((departmentName, index) => (
                      <option key={index} value={departmentName}>
                        {departmentName}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label
                    style={{
                      display: "block",
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#374151",
                      marginBottom: "8px"
                    }}
                  >
                    部署（新規入力も可能）
                  </label>
                  <input
                    type="text"
                    placeholder="新しい部署名を入力できます"
                    value={customDepartment}
                    onChange={(e) => setCustomDepartment(e.target.value)}
                    style={{
                      width: "100%",
                      height: "46px",
                      borderRadius: "12px",
                      border: "1px solid #d1d5db",
                      padding: "0 14px",
                      fontSize: "14px",
                      outline: "none",
                      boxSizing: "border-box"
                    }}
                  />
                </div>

                <div
                  style={{
                    backgroundColor: "#eff6ff",
                    border: "1px solid #bfdbfe",
                    borderRadius: "14px",
                    padding: "14px 16px",
                    color: "#1d4ed8",
                    fontSize: "13px",
                    lineHeight: 1.6
                  }}
                >
                  既存の部署は上の選択欄から選べます。新しい部署を追加したい場合は、下の入力欄に部署名を入力してください。
                </div>
              </div>
            </div>

            <div
              style={{
                padding: "18px 28px 24px 28px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                backgroundColor: "white"
              }}
            >
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  borderRadius: "12px",
                  padding: "12px 18px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: "pointer"
                }}
              >
                キャンセル
              </button>

              <button
                onClick={isEditMode ? handleUpdateUser : handleCreateUser}
                disabled={createLoading}
                style={{
                  border: "none",
                  background: "linear-gradient(135deg, #2563eb, #14b8a6)",
                  color: "white",
                  borderRadius: "12px",
                  padding: "12px 18px",
                  fontSize: "14px",
                  fontWeight: 700,
                  cursor: createLoading ? "not-allowed" : "pointer",
                  opacity: createLoading ? 0.7 : 1,
                  boxShadow: "0 8px 20px rgba(37, 99, 235, 0.18)"
                }}
              >
                {createLoading ? (isEditMode ? "更新中..." : "作成中...") : (isEditMode ? "更新する" : "作成する")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box"
};

const filterWrap: React.CSSProperties = {
  border: "1px solid #e2e8f0",
  borderRadius: 14,
  padding: 12,
  background: "#fff",
  marginBottom: 12
};

const filterGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "220px 220px 1fr 120px",
  gap: 10,
  alignItems: "end"
};

const filterLabel: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 900,
  color: "#0f172a",
  marginBottom: 6
};

const btnSecondary: React.CSSProperties = {
  height: 40,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  cursor: "pointer",
  background: "#fff",
  color: "#0f172a",
  fontWeight: 900
};

export default UsersPage;